// VERA-825 / VERA-246: Tag shop checkout page — single-screen review.
//
// Two cards side by side (matching Figma 561-259857):
//   Left  "Checkout"          — account row, shipping section, payment section
//   Right "Checkout summary"  — line items, subtotal/tax/total, Pay button
//
// Data:
//   - account      → useUserProfileQuery (name/email/avatar), same source as sidebar
//   - shipping     → useAddresses (default = is_default); "Change" opens a picker
//   - payment      → usePaymentMethods (default card); "Change" opens a picker;
//                    "Add card" falls back to the Stripe Elements add-card flow
//   - tax/shipping → useTagShopCalculate (selected address + cart line items)
//   - pay          → useTagShopCreatePaymentIntent → confirm client-side
//
// Payment model — "saved card with add-new fallback":
//   The backend POST /payments/tag-shop/create-payment-intent does NOT accept a
//   payment_method_id (see tag-shop-checkout.use-case.ts — it creates a PI on the
//   Stripe customer and returns a client_secret without confirming). So the
//   saved-card path creates the PI, then confirms client-side via
//   stripe.confirmCardPayment(clientSecret, { payment_method: pmId }). The
//   add-new-card path uses the existing Elements + TagShopPaymentForm.

import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {loadStripe} from '@stripe/stripe-js';
import {Elements, useStripe} from '@stripe/react-stripe-js';

import {CircleCheckIcon} from 'lucide-react';

import {Box, Divider, Grid, Stack, Typography} from '@mui/material';
import {Button, Skeleton} from '@repo/ui';
import {
  Header,
  CreateAddressModal,
  CreatePaymentMethodModal,
  EmptyState,
} from 'components';

import {useQueryClient} from '@tanstack/react-query';
import {useCart} from 'contexts/CartContext';
import {useCompanyStore} from 'stores/useCompanyStore';
import {useSnackbar} from 'contexts/SnackbarContext';
import {useUserProfileQuery} from 'hooks/api/useUserProfileQuery';
import {useAddresses, ADDRESSES_QUERY_KEY} from 'hooks/api/useAddresses';
import {usePaymentMethods, PAYMENT_METHODS_QUERY_KEY, PaymentMethodRow} from 'hooks/api/usePaymentMethods';
import {ROUTES} from 'routes';
import {STRIPE_PUBLIC_KEY} from 'config';
import {getNameOrEmailSlug} from 'utils';
import type {Address} from 'types';

import {
  useTagShopCalculate,
  useTagShopCreatePaymentIntent,
} from 'hooks/api/useTagShopCheckout';
import type {
  TagShopShippingAddress,
  TagShopCalculateResult,
} from 'hooks/api/useTagShopCheckout';

import {CheckoutShippingRow} from './components/CheckoutShippingRow';
import {CheckoutPaymentRow} from './components/CheckoutPaymentRow';
import {ChangeAddressModal} from './components/ChangeAddressModal';
import {ChangePaymentModal} from './components/ChangePaymentModal';
import {TagShopSummaryCard} from './components/TagShopSummaryCard';
import {TagShopPaymentForm} from './components/TagShopPaymentForm';

// Stripe promise is module-level so it's only created once (Stripe.js best
// practice — never recreate inside a render).
const stripePromise = STRIPE_PUBLIC_KEY
  ? loadStripe(STRIPE_PUBLIC_KEY as string)
  : null;

// Map a saved Address → the shape the tag-shop calculate/PI endpoints expect.
const toShippingAddress = (
  address: Address,
  buyerName: string,
): TagShopShippingAddress => {
  const [firstName, ...rest] = (address.full_name || buyerName || '')
    .trim()
    .split(' ');
  return {
    address1: address.address_line,
    city: address.city,
    province: address.state ?? '',
    country: address.country,
    zip: address.postal_code,
    firstName: firstName || undefined,
    lastName: rest.length ? rest.join(' ') : undefined,
    label: address.label || undefined,
  };
};

type CheckoutState = 'review' | 'addCard' | 'confirmed';

interface CheckoutInnerProps {
  state: CheckoutState;
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
}

const CheckoutInner = ({state, setState}: CheckoutInnerProps) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const {open: notify} = useSnackbar();
  const {items, subtotal, clear} = useCart();
  const selectedCompany = useCompanyStore((s) => s.selectedCompany);

  const queryClient = useQueryClient();
  const {data: profile} = useUserProfileQuery();
  const {addresses, isLoading: addressesLoading} = useAddresses();
  const {data: paymentData, isLoading: paymentLoading} = usePaymentMethods();
  const savedMethods = (paymentData?.methods ?? []) as PaymentMethodRow[];

  // Fall back to the loaded address's company_id when the company store isn't
  // populated (e.g. dev-session auto-login) — otherwise calculate + the payment
  // intent silently no-op on an empty companyId, leaving Tax blank.
  const companyId = selectedCompany?.id ?? addresses[0]?.company_id ?? '';

  const profileName = getNameOrEmailSlug(profile as any) ?? '';

  // ── Selection state ─────────────────────────────────────────────────────────
  const [chosenAddressId, setChosenAddressId] = useState<string | null>(null);
  const [chosenPmId, setChosenPmId] = useState<string | null>(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);

  const [calculatedTotals, setCalculatedTotals] =
    useState<TagShopCalculateResult | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // Captured from create-payment-intent so the confirmation screen can deep-link
  // to the specific order (the Shopify order id is assigned async by the webhook).
  const [reconciliationId, setReconciliationId] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState(false);

  // Resolve the currently-selected address (explicit choice → default → first).
  const selectedAddress = useMemo<Address | null>(() => {
    if (!addresses.length) return null;
    return (
      addresses.find((a) => a.id === chosenAddressId) ??
      addresses.find((a) => a.is_default) ??
      addresses[0]
    );
  }, [addresses, chosenAddressId]);

  // Resolve the currently-selected card (explicit choice → default → first).
  const selectedMethod = useMemo<PaymentMethodRow | null>(() => {
    if (!savedMethods.length) return null;
    return (
      savedMethods.find((m) => m.id === chosenPmId) ??
      savedMethods.find((m) => m.isDefault) ??
      savedMethods[0]
    );
  }, [savedMethods, chosenPmId]);

  const cartLineItems = useMemo(
    () =>
      items.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        // Flags a per-item customization setup fee (server-priced) at checkout.
        customized: !!item.customization,
      })),
    [items],
  );

  // Payment-intent items also carry the full customization (logo data URLs +
  // placement + color/finish) so the server can upload the logos and persist the
  // design on the order. Kept out of `cartLineItems` because calculate re-runs on
  // every address change and doesn't need the (large) logo payload.
  const paymentLineItems = useMemo(
    () =>
      cartLineItems.map((li, i) => ({
        ...li,
        customization: items[i]?.customization,
      })),
    [cartLineItems, items],
  );

  const calculateMutation = useTagShopCalculate();
  const createPaymentIntentMutation = useTagShopCreatePaymentIntent();

  // ── Recalculate tax/shipping whenever the selected address changes ──────────
  const lastCalcAddressId = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!selectedAddress || !companyId || items.length === 0) return;
    if (lastCalcAddressId.current === selectedAddress.id) return;
    lastCalcAddressId.current = selectedAddress.id;

    const shippingAddress = toShippingAddress(selectedAddress, profileName);
    calculateMutation.mutate(
      {companyId, items: cartLineItems, shippingAddress},
      {
        onSuccess: (result) => setCalculatedTotals(result),
        onError: (error: any) =>
          notify({
            type: 'error',
            title: 'Could not calculate tax and shipping',
            text: error?.message ?? 'Please check the address and try again.',
          }),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress?.id, companyId, items.length]);

  // ── Pay ─────────────────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!selectedAddress) {
      notify({
        type: 'error',
        title: 'Add a shipping address',
        text: 'Choose where to ship your tags before paying.',
      });
      return;
    }
    if (!selectedMethod) {
      // No saved card — drop into the add-card flow.
      handleAddCard();
      return;
    }
    if (!stripe) return;

    setIsPaying(true);
    try {
      const shippingAddress = toShippingAddress(selectedAddress, profileName);
      const firstRate =
        calculatedTotals?.availableShippingRates?.[0]?.handle ?? '';

      const pi = await createPaymentIntentMutation.mutateAsync({
        companyId,
        items: paymentLineItems,
        shippingAddress,
        shippingRateHandle: firstRate,
      });

      setReconciliationId(pi.reconciliationId);

      // The backend does not confirm with a saved payment_method_id, so confirm
      // client-side using the chosen saved Stripe PaymentMethod.
      const {error} = await stripe.confirmCardPayment(pi.clientSecret, {
        payment_method: selectedMethod.id,
      });

      if (error) {
        notify({
          type: 'error',
          title: 'Payment failed',
          text: error.message ?? 'Your card was declined. Try another card.',
        });
        setIsPaying(false);
        return;
      }

      clear();
      setState('confirmed');
    } catch (error: any) {
      notify({
        type: 'error',
        title: 'Could not complete payment',
        text: error?.message ?? 'Please try again.',
      });
    } finally {
      setIsPaying(false);
    }
  };

  // ── Add-new-card path (Stripe Elements) ─────────────────────────────────────
  const handleAddCard = async () => {
    if (!selectedAddress) {
      notify({
        type: 'error',
        title: 'Add a shipping address',
        text: 'Choose where to ship your tags before paying.',
      });
      return;
    }
    try {
      const shippingAddress = toShippingAddress(selectedAddress, profileName);
      const firstRate =
        calculatedTotals?.availableShippingRates?.[0]?.handle ?? '';
      const pi = await createPaymentIntentMutation.mutateAsync({
        companyId,
        items: paymentLineItems,
        shippingAddress,
        shippingRateHandle: firstRate,
      });
      setReconciliationId(pi.reconciliationId);
      setClientSecret(pi.clientSecret);
      setState('addCard');
    } catch (error: any) {
      notify({
        type: 'error',
        title: 'Could not start payment',
        text: error?.message ?? 'Please try again.',
      });
    }
  };

  const handlePaymentSuccess = () => {
    clear();
    setState('confirmed');
  };

  // ── Confirmation screen ─────────────────────────────────────────────────────
  // Reuses the shared EmptyState placeholder pattern (icon-in-circle, vertically
  // centered) so the success moment matches the app's empty-state style.
  if (state === 'confirmed') {
    return (
      <EmptyState
        icon={<CircleCheckIcon strokeWidth={1} size={32} />}
        title="Your order has been placed."
        text="You will receive a confirmation once your order is fulfilled."
        buttons={
          <Stack direction="row" spacing={3}>
            <Button variant="secondary" onClick={() => navigate(ROUTES.TAGS_SHOP)}>
              Back to shop
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  reconciliationId
                    ? ROUTES.TAGS_ORDER_CONFIRMATION(reconciliationId)
                    : ROUTES.TAGS_ORDERS,
                )
              }
            >
              View order
            </Button>
          </Stack>
        }
      />
    );
  }

  // ── Add-new-card screen (Stripe Elements) ───────────────────────────────────
  if (state === 'addCard' && clientSecret && stripePromise) {
    return (
      <Stack spacing={4} maxWidth={520}>
        <Elements stripe={stripePromise} options={{clientSecret}}>
          <TagShopPaymentForm
            onSuccess={handlePaymentSuccess}
            onError={(message) =>
              notify({type: 'error', title: 'Payment failed', text: message})
            }
          />
        </Elements>
        <Button
          variant="text"
          onClick={() => setState('review')}
          sx={{alignSelf: 'flex-start'}}
        >
          Back to review
        </Button>
      </Stack>
    );
  }

  // ── Review screen (two cards) ───────────────────────────────────────────────
  return (
    <>
      {/* Same grid as the cart: wide left (md=8) + narrow summary (md=4). */}
      <Grid container spacing={6} alignItems="flex-start">
        {/* Left card: Checkout — flat Box (background.paper + primary[100]),
            matching the cart's card colors (no MUI Card elevation overlay). */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
            }}
          >
            <Stack spacing={4} p={6}>
              {/* No card-level heading or "User" section — it's implied the order
                  is placed by the signed-in user (shown in the sidebar). */}
              <CheckoutShippingRow
                address={selectedAddress}
                isLoading={addressesLoading}
                onChange={() => setAddressModalOpen(true)}
                onAdd={() => setAddAddressOpen(true)}
              />

              <Divider />

              <CheckoutPaymentRow
                method={selectedMethod}
                isLoading={paymentLoading}
                onChange={() => setPaymentModalOpen(true)}
                onAdd={() => setAddPaymentOpen(true)}
              />
            </Stack>
          </Box>
        </Grid>

        {/* Right card: Checkout summary */}
        <Grid item xs={12} md={4}>
          <TagShopSummaryCard
            items={items}
            subtotal={subtotal}
            calculatedTotals={calculatedTotals}
            onPay={handlePay}
            isPaying={isPaying || createPaymentIntentMutation.isPending}
            payDisabled={items.length === 0}
          />
        </Grid>
      </Grid>

      <ChangeAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        addresses={addresses}
        selectedId={selectedAddress?.id ?? null}
        onSelect={(address) => setChosenAddressId(address.id)}
        onAddNew={() => {
          setAddressModalOpen(false);
          setAddAddressOpen(true);
        }}
      />

      <ChangePaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        methods={savedMethods}
        selectedId={selectedMethod?.id ?? null}
        onSelect={(method) => setChosenPmId(method.id)}
        onAddNew={() => {
          setPaymentModalOpen(false);
          setAddPaymentOpen(true);
        }}
      />

      {/* Inline "Add" modals — opened here on the checkout instead of navigating
          away to the settings pages. On save, invalidate the query so the new
          address/card appears and gets auto-selected (default → first). */}
      <CreateAddressModal
        open={addAddressOpen}
        onClose={() => setAddAddressOpen(false)}
        refetch={() =>
          queryClient.invalidateQueries({queryKey: ADDRESSES_QUERY_KEY})
        }
      />

      <CreatePaymentMethodModal
        open={addPaymentOpen}
        onClose={() => setAddPaymentOpen(false)}
        refetch={() =>
          queryClient.invalidateQueries({queryKey: PAYMENT_METHODS_QUERY_KEY})
        }
      />
    </>
  );
};

export const TagsShopCheckoutPage = () => {
  const navigate = useNavigate();
  const {items} = useCart();
  const [state, setState] = useState<CheckoutState>('review');

  // Redirect to cart if empty (except on the confirmation screen).
  if (items.length === 0 && state !== 'confirmed') {
    navigate(ROUTES.TAGS_SHOP_CART, {replace: true});
    return null;
  }

  // The confirmed view is the success *state* of checkout (same page), not a
  // separate "Order Placed" page — so the title/breadcrumb reads "Success".
  const title = state === 'confirmed' ? 'Success' : 'Checkout';

  return (
    <>
      <Header title={title} breadcrumbsOveride={title} />
      {/* Whole page wrapped in Elements so useStripe() is available for the
          saved-card confirm path (confirmCardPayment needs a Stripe instance,
          no clientSecret option required). */}
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <CheckoutInner state={state} setState={setState} />
        </Elements>
      ) : (
        <Stack spacing={4} alignItems="center" pt={8}>
          <Skeleton width={280} height={20} />
          <Typography variant="body2" color="text.secondary">
            Payments are not configured for this environment.
          </Typography>
        </Stack>
      )}
    </>
  );
};

export default TagsShopCheckoutPage;
